import { useRouter } from 'next/router';
import { useContext } from 'react';

import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import { UserContext } from '~/components/providers/UserProvider';
import { useContestContext } from '../context/ContestContext/ContestContext';

interface CommissionerState {
  isCommissionerMode: boolean;
  isLoggedInUserTheCommissioner: boolean;
  isSelfManagedContest: boolean;
  contestUser?: string;
  shouldAllowCommissionerAccess: boolean;
  isCommissionerPick: boolean;
  isCommissionerOwnEntry: boolean;
  shouldBypassAllGating: boolean;
}

/**
 * Hook to get all commissioner-related state and checks
 * @returns object with all commissioner state values
 */
function useIsCommissioner(): CommissionerState {
  const router = useRouter();

  const { user } = useContext(UserContext);
  const { contest } = useContestContext();

  const isCommissionerMode = router.query[URLQueryKeys.IS_COMMISSIONER] === 'true';
  const isLoggedInUserTheCommissioner = contest?.commissioner?.id === user?.id;
  const isSelfManagedContest = Boolean(
    contest?.settings?.entertainmentOnlySettings?.enabled && contest?.manage_entrants
  );
  const contestUser = router.query[URLQueryKeys.CONTEST_USER] as string;
  const shouldAllowCommissionerAccess =
    isCommissionerMode && isLoggedInUserTheCommissioner && isSelfManagedContest;
  const isCommissionerPick = shouldAllowCommissionerAccess && isCommissionerMode;

  // Check if the commissioner is accessing their own entry
  const isCommissionerOwnEntry = Boolean(contestUser && contest?.commissioner?.id === contestUser);

  // Determine if all gating should be bypassed
  // This happens when the commissioner is accessing their own entry
  const shouldBypassAllGating = isCommissionerPick && isCommissionerOwnEntry;

  return {
    isCommissionerMode,
    isLoggedInUserTheCommissioner,
    isSelfManagedContest,
    contestUser,
    shouldAllowCommissionerAccess,
    isCommissionerPick,
    isCommissionerOwnEntry,
    shouldBypassAllGating,
  };
}

export default useIsCommissioner;
